/**
 * When the onboarding MODAL is allowed to open.
 *
 * The old rule was one line — a permanent `seen` flag written on any dismissal —
 * and it lost people: a reflexive tap on the overlay in the first two seconds
 * killed onboarding for that user on that device forever, before they had seen
 * a single tender. Measured on prod over the same 6-day window: 125 signups,
 * 139 saved searches, but only 21 `onboarding_completed`.
 *
 * Two rules replace it.
 *
 * **Cap of 3 showings.** Sized against how people actually return: 64.5% of
 * registered users are active on exactly ONE day, 17.8% on two, 6.1% on three.
 * So the 2nd showing is the one that does the work and the 3rd reaches ~18% of
 * users. (I would have stopped at 2 — the marginal showing lands mostly on the
 * most engaged users, who are the ones least worth nagging — but 3 is the
 * product call, and the per-day rule below is what keeps it from being felt.)
 *
 * **At most once per CALENDAR DAY.** Users average 2.1 sessions per active day,
 * one as high as 93. A "show again next session" rule would therefore re-open
 * the modal minutes after a dismissal, in the same afternoon — which is exactly
 * the behaviour that teaches people to reflex-dismiss it. A day boundary
 * guarantees the next showing lands in a genuinely new visit.
 *
 * The permanent flag now means only one thing: onboarding is COMPLETE. It is
 * never written on a dismissal.
 */

export const ONBOARDING_GATE_KEY = 'uztender_onboarding_v2';
/** The v1 flag, written on ANY dismissal. Read once, to migrate. */
export const LEGACY_SEEN_KEY = 'uztender_onboarding_seen_v1';

export const MAX_SHOWINGS = 3;

export interface OnboardingGateState {
  /** How many times the modal has been opened. */
  shown: number;
  /** `YYYY-MM-DD` of the last opening, local time. */
  lastShownDate: string | null;
  /** Set ONLY when every step is done. Never on dismissal. */
  complete: boolean;
  /** The user answered "I have no ИНН"; that step is retired for good. */
  innSkipped: boolean;
  /** The catalog strip was collapsed by hand. Defaults to EXPANDED: collapsed
   *  by default meant most users never saw the steps at all, which is the whole
   *  point of the strip. Once someone collapses it, that sticks. */
  bannerCollapsed: boolean;
}

const EMPTY: OnboardingGateState = {
  shown: 0,
  lastShownDate: null,
  complete: false,
  innSkipped: false,
  bannerCollapsed: false,
};

export function readGate(storage: Storage | undefined = safeStorage()): OnboardingGateState {
  // No usable storage → treat as a FRESH user, not a finished one.
  //
  // Returning `complete` here would be the safer-looking choice and is wrong:
  // it silently disables onboarding entirely for anyone with localStorage
  // blocked (private mode, hardened browsers) — they would never be asked to
  // save a search or connect Telegram at all. v1 behaved this way too
  // (`catch { return false }`), so this preserves it rather than regressing.
  //
  // The cost is bounded: without storage the cap cannot persist, but the
  // component's own in-memory `dismissed` state still closes it for the visit,
  // so the worst case is one showing per page load for a small minority.
  if (!storage) return { ...EMPTY };
  try {
    const raw = storage.getItem(ONBOARDING_GATE_KEY);
    if (raw) return { ...EMPTY, ...(JSON.parse(raw) as Partial<OnboardingGateState>) };

    // Migration. A v1 dismissal was usually accidental and always permanent, so
    // treating it as `complete` would keep punishing exactly the people this
    // change exists to recover. Count it as ONE showing instead: they keep two
    // more chances under the new policy, and nobody starts from zero.
    if (storage.getItem(LEGACY_SEEN_KEY) === '1') {
      return { ...EMPTY, shown: 1 };
    }
    return { ...EMPTY };
  } catch {
    return { ...EMPTY };
  }
}

export function writeGate(
  next: OnboardingGateState,
  storage: Storage | undefined = safeStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(ONBOARDING_GATE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — the modal simply behaves as if it had never shown */
  }
}

/** `YYYY-MM-DD` in LOCAL time. Injected in tests; never derived from UTC, or the
 *  "once a day" boundary would move for users depending on their timezone. */
export function today(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * May the modal open right now?
 *
 * `hasPendingSteps` is passed in rather than read here: with nothing left to
 * ask, the modal must not open even if the cap has room.
 */
export function mayShowModal(
  gate: OnboardingGateState,
  hasPendingSteps: boolean,
  now: Date = new Date(),
): boolean {
  if (gate.complete) return false;
  if (!hasPendingSteps) return false;
  if (gate.shown >= MAX_SHOWINGS) return false;
  if (gate.lastShownDate === today(now)) return false;
  return true;
}

/** Record an opening. Called once per actual open, not per render. */
export function recordShown(
  gate: OnboardingGateState,
  now: Date = new Date(),
): OnboardingGateState {
  return { ...gate, shown: gate.shown + 1, lastShownDate: today(now) };
}

function safeStorage(): Storage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
